/* hs-eslint ignored failing-rules */
/* eslint-disable react/prop-types */

'use es6';

import { connect } from 'general-store';
import { Map as ImmutableMap } from 'immutable';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import { useHistory } from 'react-router-dom';
import { isNil } from 'dashboard-lib/private/js-util';
import PortalIdParser from 'PortalIdParser';
import compose from 'transmute/compose';
import setDocumentTitle from 'I18n/utils/setDocumentTitle';
import { markActionComplete } from 'user-context/onboarding';
import { commerceReportingDashboard } from 'getting-started-shared-tasks/taskNames';
import { getChecker } from 'dashboard-lib/private/action-problem-solution/common/problem';
import { CUSTOM_DASHBOARD_COUNT, CUSTOM_DASHBOARD_LIMIT, DASHBOARD_CREATED_AT_TIMESTAMP, DASHBOARD_IS_CUSTOM } from 'dashboard-lib/private/action-problem-solution/params/all-params';
import { CUSTOM_DASHBOARD_LIMIT_EXCEEDED_AND_DASHBOARD_SHOULD_LOCKDOWN } from 'dashboard-lib/private/action-problem-solution/problems/lockdown-problem';
import { isCustomDashboard } from 'dashboard-lib/public/dashboard/dashboard-logic';
import { getRecentDashboards } from 'dashboard-lib/public/dashboard/dashboard-dao';
import { getCreatedAt } from 'dashboard-lib/public/dashboard/dashboard-schema';
import { derivePermission } from 'dashboard-lib/public/dashboard/dashboard-permission';
import { AssetPermission } from 'dashboard-lib/public/permission/asset-permission';
import { selectHasReportingAccess } from 'dashboard-lib/public/userInfo/scopes';
import { useCustomDashboardLimit } from 'dashboard-components/context/use-custom-dashboard-limit';
import { useInitializeFilters } from 'dashboard-ui-components/initializeFilters/use-initialize-filters';
import { useRecurringEmailDialogParam } from 'dashboard-ui-components/hooks/useRecurringEmailDialogParam';
import ApplicationAccessDenied from 'dashboard-components/disabled-message/common/application-access-denied';
import toClass from '../../decorators/toClass';
import withDashboardErrorBoundary from '../../decorators/withDashboardErrorBoundary';
import { DashboardDependency } from '../../dependencies/DashboardDependency';
import DashboardsDependency, { DashboardCountDependency } from '../../dependencies/DashboardsDependency';
import * as DashboardRouter from '../../lib/dashboard-router';
import { parseDashboardId } from '../../lib/DashboardRetriever';
import { getPathParams } from '../../lib/routerUtils';
import SettingsStore from '../../stores/SettingsStore';
import Dashboard from '../dashboard/Dashboard';
import NotFound from '../NotFound';
import LoadingMessage from '../shared/LoadingMessage';
import { USER_ATTRIBUTES_KEYS } from '../../data/UserAttributesDAO';
import ForbiddenDashboardPage from '../../pages/ForbiddenDashboardPage';
import DashboardStore from '../../stores/DashboardStore';
import { parseAsQueryMap } from '../../lib/historyUtils';
import { createUrlUnsafe } from '../../lib/pathUtils';
import { DASHBOARD_ACCESS_DENIED, ForbiddenAssetTypesByErrorType } from '../../constants/ForbiddenAssets';
import DashboardCollaborationSidebar from '../DashboardCollaborationSidebar';
import { FiltersMetadataLoadFailureAlert } from '../../initializeFilters/FiltersMetadataLoadFailureAlert';
import { getQueryString } from '../../lib/historyUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DASHBOARD_LIBRARY_URL = `/discover/${PortalIdParser.get()}/library/dashboards`;
const getDashboardIdFromPath = match => {
  const {
    dashboardId: dashboardIdFromPath
  } = getPathParams(match);
  return parseDashboardId(dashboardIdFromPath);
};
const isViewableDashboardId = (typeOrId, dashboards) => {
  if (isNil(typeOrId) || isNaN(Number(typeOrId))) {
    return false;
  }
  const dashboard = dashboards.get(typeOrId);
  if (!dashboard) {
    return false;
  } else {
    return derivePermission(dashboard) !== AssetPermission.ZERO;
  }
};
const getFirstViewableDashboardId = (dashboardIds, dashboards) => {
  return dashboardIds.find(typeOrId => isViewableDashboardId(typeOrId, dashboards));
};
const LoadingContainer = props => {
  useEffect(() => {
    setDocumentTitle('app.dashboardTitle');
  }, []);
  const {
    match,
    dashboards,
    dashboardFetched,
    settings: {
      userAttributesLoaded
    }
  } = props;
  const history = useHistory();
  const dashboardId = getDashboardIdFromPath(match);
  const isLoadingCurrentDashboard = dashboardId !== 'default' && !dashboardFetched; // Prevent the loading page from blinking if dashboard is a forbidden asset
  const isLoadingDefaultDashboard = dashboardId === 'default' && !dashboards.get('fetched');
  const hasReportingAccess = useSelector(selectHasReportingAccess);
  if (!hasReportingAccess) {
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "ACCESS_DENIED_PAGE_LOADED",
      children: /*#__PURE__*/_jsx(ApplicationAccessDenied, {})
    });
  }
  if (isLoadingCurrentDashboard || isLoadingDefaultDashboard || !userAttributesLoaded) {
    return /*#__PURE__*/_jsx(LoadingMessage, {});
  }
  const newProps = Object.assign({}, props, {
    dashboardId,
    dashboards: dashboards.get('dashboards'),
    history
  });
  if (dashboardId === 'default') {
    return /*#__PURE__*/_jsx(DefaultDashboardRedirect, Object.assign({}, newProps));
  }
  return /*#__PURE__*/_jsx(DashboardContainer, Object.assign({}, newProps));
};
const asyncRouteToDashboard = (dashboardId, history) => {
  window.setTimeout(() => DashboardRouter.routeToDashboard(dashboardId, true, history));
};
const DefaultDashboardRedirect = props => {
  const {
    dashboards,
    settings: {
      userAttributes
    }
  } = props;
  const history = useHistory();
  const userPreferredHome = userAttributes.get(USER_ATTRIBUTES_KEYS.PREFERRED_HOME);
  const recentDashboardIds = getRecentDashboards();
  if (isViewableDashboardId(userPreferredHome, dashboards)) {
    asyncRouteToDashboard(userPreferredHome, history);
  } else {
    const firstViewableRecentDashboardId = recentDashboardIds && !recentDashboardIds.isEmpty() && getFirstViewableDashboardId(recentDashboardIds, dashboards);
    const firstViewableDashboardId = dashboards && !dashboards.isEmpty() && getFirstViewableDashboardId(dashboards.keySeq(), dashboards);
    if (firstViewableRecentDashboardId) {
      asyncRouteToDashboard(firstViewableRecentDashboardId, history);
    } else if (firstViewableDashboardId) {
      asyncRouteToDashboard(firstViewableDashboardId, history);
    } else {
      window.location = createUrlUnsafe({
        template: DASHBOARD_LIBRARY_URL,
        queryParam: parseAsQueryMap(history)
      });
    }
  }
  return null;
};
const RenderDashboard = props => {
  useInitializeFilters(props.dashboard);
  useRecurringEmailDialogParam(props.dashboard);
  return /*#__PURE__*/_jsx(Dashboard, Object.assign({}, props));
};
RenderDashboard.propTypes = Dashboard.propTypes;
const COMMERCE_REPORTING_DASHBOARD_TASK_COMPLETION_DELAY = 3000;
const DashboardContainer = ({
  dashboardId,
  dashboards,
  dashboard,
  dashboardPermissions,
  settings,
  customDashboardCount
}) => {
  const dashboardExists = dashboards.get(dashboardId) != null;
  const customDashboardLimit = useCustomDashboardLimit();
  const checkDashboardLocked = getChecker(CUSTOM_DASHBOARD_LIMIT_EXCEEDED_AND_DASHBOARD_SHOULD_LOCKDOWN);
  const dashboardLocked = checkDashboardLocked({
    [DASHBOARD_IS_CUSTOM]: isCustomDashboard(dashboard),
    [DASHBOARD_CREATED_AT_TIMESTAMP]: getCreatedAt(dashboard),
    [CUSTOM_DASHBOARD_COUNT]: customDashboardCount,
    [CUSTOM_DASHBOARD_LIMIT]: customDashboardLimit
  });
  const {
    errorType,
    isForbidden
  } = dashboardPermissions;
  const isDashboardForbidden = isForbidden && errorType && errorType !== DASHBOARD_ACCESS_DENIED;
  const history = useHistory();
  const viaCommerceUserGuide = getQueryString(history).includes('via=commerce-hub-user-guide');
  useEffect(() => {
    if (viaCommerceUserGuide) {
      setTimeout(() => markActionComplete(commerceReportingDashboard, {
        showTaskCompletionModal: true
      }), COMMERCE_REPORTING_DASHBOARD_TASK_COMPLETION_DELAY);
    }
  }, [viaCommerceUserGuide]);
  if (isDashboardForbidden) {
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "FORBIDDEN_ACCESS_DASHBOARD",
      children: /*#__PURE__*/_jsx(ForbiddenDashboardPage, {
        assetType: ForbiddenAssetTypesByErrorType[errorType],
        dashboardId: dashboardId
      })
    });
  }
  if (!dashboardExists) {
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "DASHBOARD_NOT_FOUND",
      children: /*#__PURE__*/_jsx(NotFound, {
        dashboard: dashboard
      })
    });
  }
  return /*#__PURE__*/_jsxs(DashboardCollaborationSidebar, {
    dashboardId: dashboardId,
    children: [/*#__PURE__*/_jsx(FiltersMetadataLoadFailureAlert, {
      dashboard: dashboard
    }), /*#__PURE__*/_jsx(RenderDashboard, Object.assign({
      customDashboardLimit: customDashboardLimit,
      dashboardId: dashboardId,
      dashboardLocked: dashboardLocked,
      dashboard: dashboard,
      dashboards: dashboards
    }, settings))]
  });
};
DashboardContainer.propTypes = {
  history: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  dashboard: PropTypes.instanceOf(ImmutableMap),
  dashboards: PropTypes.instanceOf(ImmutableMap).isRequired,
  customDashboardCount: PropTypes.number,
  settings: PropTypes.object.isRequired
};
const storeDependencies = {
  dashboard: DashboardDependency,
  dashboards: DashboardsDependency,
  dashboardPermissions: {
    stores: [DashboardStore],
    deref() {
      return {
        errorType: DashboardStore.get('errorType'),
        isForbidden: DashboardStore.get('isForbidden')
      };
    }
  },
  dashboardFetched: {
    stores: [DashboardStore],
    deref({
      match
    }) {
      const dashboardId = getDashboardIdFromPath(match);
      return !!DashboardStore.get(['dashboardFetched', dashboardId]);
    }
  },
  customDashboardCount: DashboardCountDependency,
  settings: {
    stores: [SettingsStore],
    deref() {
      return {
        userAttributesLoaded: SettingsStore.get('user-attributes-loaded'),
        userAttributes: SettingsStore.get('user-attributes')
      };
    }
  }
};
export default compose(withDashboardErrorBoundary, connect(storeDependencies), toClass)(LoadingContainer);